import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { SupplementalDataSetResumeGradingRubricV2 } from 'libs/end-to-end/src/types/databases/data-sets/supplemental-data-set-config';
import { Spacing } from 'apps/sigma2-ui/src/components/ui/Spacing';
import { Modal } from 'apps/sigma2-ui/src/components/ui/Modal';
import { useRubricConstructsData } from 'apps/sigma2-ui/src/data-wizard/components/use-rubric-constructs-data';
import { UseRubricConstructsDataPayload } from 'apps/sigma2-ui/src/data-wizard/components/use-rubric-constructs-data';
import { DatabaseRubricBankConstructsView } from 'apps/sigma2-ui/src/data-wizard/components/DatabaseRubricBankConstructsView';
import { useCrudOperations } from 'apps/sigma2-ui/src/data-wizard/components/composite-dataset/recipes/use-crud-operations';
import { UseCrudOperationsPayload } from 'apps/sigma2-ui/src/data-wizard/components/composite-dataset/recipes/use-crud-operations';
import { DataSetSupplementalResumeGradingV2ConfigurationComponent } from 'apps/sigma2-ui/src/data-wizard/components/DataSetSupplementalResumeGradingV2ConfigurationComponent';
import { DataSetEditSupplementalHook } from 'apps/sigma2-ui/src/data-wizard/lib/useDataSetEditSupplemental';
import { Button } from 'apps/sigma2-ui/src/components/ui/Button';

export const useSupplementalRubricConstructsModalConfigs = ({
  rubricsByJobTitle,
  setRubricsByJobTitle,
}: {
  rubricsByJobTitle: SupplementalDataSetResumeGradingRubricV2[];
  setRubricsByJobTitle: (rubrics: SupplementalDataSetResumeGradingRubricV2[]) => void;
}): {
  showConstructsModal: boolean;
  setShowConstructsModal: (show: boolean) => void;
  currentRubricConfigIndex: number | null;
  setCurrentRubricConfigIndex: (index: number | null) => void;
  useRubricConstructsDataPayload: UseRubricConstructsDataPayload;
  rubricConfigOps: UseCrudOperationsPayload<SupplementalDataSetResumeGradingRubricV2>;
} => {
  const { organizationId, databaseId } = useParams();
  const [showConstructsModal, setShowConstructsModal] = useState(false);
  const [currentRubricConfigIndex, setCurrentRubricConfigIndex] = useState<
    number | null
  >(null);

  const useRubricConstructsDataPayload = useRubricConstructsData({
    organizationId: organizationId!,
    databaseId: databaseId!,
    isSelectEnabled: true,
  });

  const rubricConfigOps = useCrudOperations({
    items: rubricsByJobTitle,
    setItems: (items) => {
      setRubricsByJobTitle(items);
    },
    postProcessNewItems: (newItems) => newItems,
  });

  return {
    showConstructsModal,
    setShowConstructsModal,
    useRubricConstructsDataPayload,
    rubricConfigOps,
    currentRubricConfigIndex,
    setCurrentRubricConfigIndex,
  };
};

export const SupplementalRubricConstructsModal = ({
  showConstructsModal,
  setShowConstructsModal,
  currentRubricConfigIndex,
  setCurrentRubricConfigIndex,
  useRubricConstructsDataPayload,
  rubricConfigOps,
}: {
  showConstructsModal: boolean;
  setShowConstructsModal: (show: boolean) => void;
  currentRubricConfigIndex: number | null;
  setCurrentRubricConfigIndex: (index: number | null) => void;
  useRubricConstructsDataPayload: UseRubricConstructsDataPayload;
  rubricConfigOps: UseCrudOperationsPayload<SupplementalDataSetResumeGradingRubricV2>;
}) => {
  return (
    <Modal
      show={showConstructsModal}
      onClose={() => {
        setShowConstructsModal(false);
        setCurrentRubricConfigIndex(null);
      }}
      title={'Choose Rubric Items'}
      maxWidthOverride="max-w-screen-md"
      scrollable={true}
    >
      <DatabaseRubricBankConstructsView
        payload={useRubricConstructsDataPayload}
        isEditable={false}  
      />

      <Button
        behavior={{
          type: 'onClick',
          onClick: () => {
            if (
              currentRubricConfigIndex !== null &&
              useRubricConstructsDataPayload.singleSelectConfig
                ?.currentConstructId
            ) {
              rubricConfigOps.onEdit(currentRubricConfigIndex, {
                rubricConstructId:
                  useRubricConstructsDataPayload.singleSelectConfig
                    .currentConstructId,
              });
            }

            setShowConstructsModal(false);
            setCurrentRubricConfigIndex(null);
          },
        }}
        variant={'primary'}
        size={'small'}
        text={'Save'}
      />
    </Modal>
  );
};

export const DataSetSupplementalConfigurationComponent = ({
  supplementalHook,
}: {
  supplementalHook: DataSetEditSupplementalHook;
}) => {

  const { config, setConfig } = supplementalHook;

  const modalConfigs = useSupplementalRubricConstructsModalConfigs({
    rubricsByJobTitle: config.supplementalDataSetType === 'resumes' ? (config.rubricsByJobTitle ?? []) : [],
    setRubricsByJobTitle: (rubrics) => {
      if (config.supplementalDataSetType === 'resumes') {
        setConfig({
          ...config,
          rubricsByJobTitle: rubrics,
        });
      }
    },
  });
  
  return (
    <Spacing size={'lg'} direction={'vertical'}>
      <SupplementalRubricConstructsModal
        showConstructsModal={modalConfigs.showConstructsModal}
        setShowConstructsModal={modalConfigs.setShowConstructsModal}
        currentRubricConfigIndex={modalConfigs.currentRubricConfigIndex}
        setCurrentRubricConfigIndex={modalConfigs.setCurrentRubricConfigIndex}
        useRubricConstructsDataPayload={modalConfigs.useRubricConstructsDataPayload}
        rubricConfigOps={modalConfigs.rubricConfigOps}
      />

      {config.supplementalDataSetType === 'resumes' && (
        <DataSetSupplementalResumeGradingV2ConfigurationComponent
          config={config}
          setConfig={setConfig}
          rubricConfigOps={modalConfigs.rubricConfigOps}
          useRubricConstructsDataPayload={modalConfigs.useRubricConstructsDataPayload}
          setCurrentRubricConfigIndex={modalConfigs.setCurrentRubricConfigIndex}
          setShowConstructsModal={modalConfigs.setShowConstructsModal}
        />
      )}
    </Spacing>
  );
};
